// src/components/FirmCompletionHost.js — the "finish this new firm" window
// (Travis, 10/21). A firm named in the person form that isn't in the CRM is
// saved with its name only (POST /api/firms/pending) so the person can be
// linked to it. Right after the person is saved, this opens the New Firm
// form for it, and it cannot be closed until the required fields (name and
// firm type) are saved. Any firm still missing its type also opens it when
// the CRM loads, so an unfinished firm never gets left behind.
//
// Mounted once in the CRM layout; PersonForm calls requestFirmCompletion().
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { FirmForm } from '../pages/Firms'

const EVENT = 'rga:complete-firms'

export function requestFirmCompletion(firmIds) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: firmIds }))
}

export default function FirmCompletionHost() {
  const qc = useQueryClient()
  const [queue, setQueue] = useState([])
  const [warn, setWarn] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const { data: firms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: users = [] } = useQuery({
    queryKey: ['relationship-owners'],
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
  })

  const add = ids => setQueue(q => [...q, ...ids.filter(id => !q.includes(id))])

  useEffect(() => {
    const onRequest = e => add(e.detail ?? [])
    window.addEventListener(EVENT, onRequest)
    return () => window.removeEventListener(EVENT, onRequest)
  }, [])

  // Firms saved with a name only and never finished.
  useEffect(() => {
    const unfinished = firms.filter(f => Array.isArray(f.firm_type) && f.firm_type.length === 0).map(f => f.firm_id)
    if (unfinished.length) add(unfinished)
  }, [firms])

  const firmId = queue[0]
  const firm = firms.find(f => f.firm_id === firmId)
  const { data: fetched } = useQuery({
    queryKey: ['firm', firmId],
    queryFn: () => api.get(`/api/firms/${firmId}`).then(r => r.data.data),
    enabled: !!firmId && !firm,
  })
  const current = firm ?? fetched
  if (!firmId || !current) return null

  const next = () => { setQueue(q => q.slice(1)); setWarn(false); setError(null) }

  async function save(payload) {
    setSaving(true); setError(null)
    try {
      await api.patch(`/api/firms/${firmId}`, payload)
      await qc.invalidateQueries({ queryKey: ['firms'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      next()
    } catch (err) {
      setError(err.response?.data?.error || err.message)
    } finally {
      setSaving(false)
    }
  }

  // Closing is refused until the firm has its required fields.
  const tryClose = () => setWarn(true)

  return (
    <div onClick={e => e.target === e.currentTarget && tryClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div role="dialog" aria-modal="true" style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
        width: '100%', maxWidth: 560, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>New Firm: {current.name}</h2>
            <div style={{ fontSize: 12, color: warn ? 'var(--danger-text)' : 'var(--text-secondary)', marginTop: 2 }}>
              {warn
                ? 'Choose at least one firm type and save before closing. The firm is already saved with its name.'
                : 'This firm is new to the CRM. Add its details; firm type is required.'}
            </div>
          </div>
          <button className="btn-icon" onClick={tryClose} style={{ fontSize: 16 }} aria-label="Close">&#10005;</button>
        </div>
        <FirmForm
          key={firmId}
          initial={current}
          users={users}
          onSubmit={save}
          onCancel={tryClose}
          submitting={saving}
          error={error}
        />
      </div>
    </div>
  )
}
